/**
 * ADR-210 — the permanent link to a project file, built the way the Go side
 * builds it (handler.PermanentFilePath / cleanSharePath). The two are held to the
 * same table in test/fileLinks.contract.test.ts.
 *
 * Why the browser builds one at all: `copy-link` must write to the clipboard in
 * the same user gesture the plugin's click started. Safari drops that activation
 * across a network round trip, so the copy path does not wait for the server.
 * The server (`share_link` op) answers the same string for a plugin that only
 * wants to DISPLAY the link.
 */

/** Percent-encode one path segment: only unreserved characters stay literal, plus a
 *  non-final dot. `!'()*` and a trailing dot are encoded so a chat client that trims
 *  trailing punctuation from a pasted URL cannot cut it short. */
export function encodeLinkSegment(s: string): string {
  const bytes = new TextEncoder().encode(s)
  let out = ''
  bytes.forEach((c, i) => {
    const keep =
      (c >= 0x61 && c <= 0x7a) || (c >= 0x41 && c <= 0x5a) || (c >= 0x30 && c <= 0x39) ||
      c === 0x2d || c === 0x5f || c === 0x7e || (c === 0x2e && i !== bytes.length - 1)
    out += keep ? String.fromCharCode(c) : `%${c.toString(16).toUpperCase().padStart(2, '0')}`
  })
  return out
}

/** `/files/<scope>/<seg>/<seg>` with each segment encoded. */
export function permanentFilePath(scope: string, relPath: string): string {
  return `/files/${encodeLinkSegment(scope)}/${relPath.split('/').map(encodeLinkSegment).join('/')}`
}

/** True when `p` is an acceptable scope-relative file path. Never "fixes" a path. */
export function isValidSharePath(p: string): boolean {
  if (!p || p.length > 4096) return false
  if (p.startsWith('/') || p.endsWith('/') || p.includes('\\')) return false
  if (/[\u0000-\u001f\u007f]/.test(p)) return false
  return p.split('/').every((seg) => {
    const low = seg.toLowerCase()
    return seg !== '' && !seg.startsWith('.') && !low.startsWith('%2e') && !low.includes('%2f') && !low.includes('%5c')
  })
}
